import React, { useRef, useState } from "react";
import { LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import styles from "./GabSyncManagement.module.css";

interface Props {
  targetLabel: string;
  inserts: number;
  overwrites: number;
  customs: number;
  applying: boolean;
  error?: string | null;
  onApply: (confirmation: string) => void;
  onBack: () => void;
}

// Deliberately heavier than a one-click confirm: the operator must type the exact target environment
// name before Apply enables. The server independently re-checks this, so the UI gate is convenience,
// not the security boundary.
function ApplyPanel({ targetLabel, inserts, overwrites, customs, applying, error, onApply, onBack }: Props) {
  const [confirmText, setConfirmText] = useState("");
  // @loffa LButton snapshots its props at construction and never refreshes onClick (only disabled /
  // className / children are read live), so a closure over confirmText freezes at its mount value ("")
  // and Apply posts an empty confirmation. Read the live value from a ref so the frozen handler still
  // sends what's actually typed.
  const confirmRef = useRef("");
  confirmRef.current = confirmText;
  const matches = confirmText.trim().toLowerCase() === (targetLabel ?? "").trim().toLowerCase();

  return (
    <div className={styles.applyContainer}>
      {error && (
        <div className={styles.applyError}>
          <FontAwesomeIcon icon={faTriangleExclamation} className={styles.applyErrorIcon} />
          <div>
            <div className={styles.applyErrorTitle}>Apply did not run — nothing was written</div>
            <div className={styles.applyErrorBody}>{error}</div>
          </div>
        </div>
      )}

      <div className={styles.applyWarning}>
        <FontAwesomeIcon icon={faTriangleExclamation} className={styles.applyWarningIcon} />
        <div>
          <div className={styles.applyWarningTitle}>
            You are about to write to <strong>{targetLabel}</strong>.
          </div>
          <div className={styles.applyWarningBody}>
            {inserts} row(s) will be inserted and {overwrites} existing row(s) will be overwritten
            {customs > 0 && <span> ({customs} hand-edited)</span>}. A snapshot of each affected table
            is taken first so the change can be rolled back. This cannot be undone automatically.
          </div>
        </div>
      </div>

      <div className={styles.confirmField}>
        <label className={styles.confirmLabel}>
          Type the target environment name <strong>{targetLabel}</strong> to enable Apply:
        </label>
        <input
          type="text"
          className={styles.confirmInput}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={targetLabel}
          disabled={applying}
          autoComplete="off"
        />
      </div>

      <div className={styles.stepNav}>
        <LButton look="flat" onClick={onBack} disabled={applying}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </LButton>
        <LButton
          themeColor="primary"
          className="red"
          disabled={!matches || applying}
          onClick={() => onApply(confirmRef.current.trim())}
        >
          {applying ? "Applying…" : `Apply to ${targetLabel}`}
        </LButton>
      </div>
    </div>
  );
}

export { ApplyPanel };
